<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});
const emit = defineEmits(['navigate']);

const emptyRuntime = {
  actions: { finance: {} },
  data: ref({}),
  error: ref(''),
  loading: ref(false),
  saving: ref(false),
  selectedId: ref(''),
};
const realData = inject('imudeiRealData', null) || emptyRuntime;
const runtimeModel = useImudeiRuntimeModel({
  realData,
  module: computed(() => 'finance'),
  screen: computed(() => 'dashboard'),
});

const searchQuery = ref('');
const selectedId = ref('');
const selectedStatus = ref('');
const selectedType = ref('');
const dueFrom = ref('');
const dueTo = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const valueAt = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => valueAt(value, path))
    .find(item => item !== undefined && item !== null && item !== '');
  return found ?? fallback;
};
const displayText = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    return firstValue(value, ['label', 'name', 'formatted', 'display'], '—');
  }
  return String(value);
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const formatMoney = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    const formatted = firstValue(value, ['formatted', 'display', 'label']);
    if (formatted) return formatted;
    return formatMoney(value.amount ?? value.value ?? value.amount_cents);
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  }).format(amount);
};
const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/pago|conclu|concili|confirm/.test(status)) return 'bg-emerald-50 text-emerald-700';
  if (/cancel/.test(status)) return 'bg-violet-100 text-violet-700';
  if (/atras|venc/.test(status)) return 'bg-rose-50 text-rose-600';
  if (/pend|aguard|prev/.test(status)) return 'bg-orange-50 text-orange-600';
  return 'bg-slate-100 text-slate-600';
};
const dotTone = index =>
  ['bg-[#0ea89c]', 'bg-[#ff9711]', 'bg-[#ff393e]', 'bg-[#af99e4]'][
    index % 4
  ];
const progressClassFor = value => {
  if (!Number.isFinite(value)) return 'w-0';
  if (value >= 75) return 'w-full';
  if (value >= 50) return 'w-3/4';
  if (value >= 25) return 'w-1/2';
  return 'w-1/4';
};

const financeData = computed(() => realData.data.value || {});
const sourceRecords = computed(() =>
  props.records.length ? props.records : runtimeModel.records.value || []
);
const sourceMetrics = computed(() =>
  props.metrics.length ? props.metrics : runtimeModel.metrics.value || []
);
const sourceHistory = computed(() => {
  if (props.history.length) return props.history;
  if (runtimeModel.history.value?.length) return runtimeModel.history.value;
  return asArray(financeData.value.history);
});
const loading = computed(() => Boolean(realData.loading?.value));
const error = computed(() => realData.error?.value || '');

const rows = computed(() =>
  sourceRecords.value
    .map(record => {
      const source = record.source || record || {};
      const address = source.endereco || source.parcela?.endereco || {};
      const addressLabel = [
        address.logradouro,
        address.numero,
        address.unidade,
        address.bairro,
      ]
        .filter(Boolean)
        .join(' · ');
      const id = String(record.id || source.id || source.parcela_id || '');
      const status =
        record.status ||
        displayText(firstValue(source, ['status.label', 'parcela.status.label', 'status']));

      return {
        amount:
          record.amount ||
          formatMoney(firstValue(source, ['valor_parcela', 'parcela.valor_parcela', 'amount'])),
        beneficiary: firstValue(source, [
          'beneficiary.name',
          'beneficiario.name',
          'favorecido.name',
          'payee.name',
          'usuarios_agrupados.0.nome',
          'usuarios_agrupado.0.usuario.name',
          'manager.name',
          'dados_processo.manager.name',
        ]),
        dueAt: firstValue(source, [
          'data_prevista',
          'parcela.data_prevista',
          'due_at',
          'due_date',
        ]),
        id,
        installment: firstValue(source, [
          'numero_parcela',
          'parcela.numero_parcela',
          'parcela.numero',
          'sequence',
        ]),
        installmentTotal: firstValue(source, [
          'total_parcelas',
          'parcela.total_parcelas',
          'installments_total',
        ]),
        manager: firstValue(source, [
          'manager.name',
          'dados_processo.manager.name',
          'responsible.name',
        ]),
        property:
          record.title ||
          firstValue(source, [
            'dados_processo.titulo',
            'processo.titulo',
            'property.title',
            'imovel.titulo',
          ]) ||
          addressLabel ||
          'Imóvel não informado',
        propertyAddress: addressLabel,
        reference: firstValue(source, [
          'dados_processo.codigo',
          'processo.codigo',
          'transaction.reference',
          'reference',
          'processo_id',
        ]),
        saleType: firstValue(source, ['tipo_venda', 'kind', 'movement_type']),
        source,
        status,
        store: firstValue(source, ['loja.name', 'dados_processo.loja.name']),
      };
    })
    .filter(row => row.id)
);

watch(
  rows,
  list => {
    if (!list.some(row => row.id === selectedId.value)) {
      selectedId.value = list[0]?.id || '';
    }
  },
  { immediate: true }
);

const statusOptions = computed(() =>
  [...new Set(rows.value.map(row => row.status).filter(Boolean))]
    .sort((first, second) => first.localeCompare(second, 'pt-BR'))
    .slice(0, 12)
);
const typeOptions = computed(() =>
  [...new Set(rows.value.map(row => row.saleType).filter(Boolean))]
    .sort((first, second) => first.localeCompare(second, 'pt-BR'))
    .slice(0, 12)
);
const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  return rows.value.filter(row => {
    const matchesQuery =
      !query ||
      `${row.reference} ${row.property} ${row.propertyAddress} ${row.beneficiary} ${row.manager} ${row.installment}`
        .toLocaleLowerCase('pt-BR')
        .includes(query);
    const dueAt = row.dueAt ? new Date(row.dueAt) : null;
    const matchesFrom =
      !dueFrom.value ||
      (dueAt && dueAt >= new Date(`${dueFrom.value}T00:00:00Z`));
    const matchesTo =
      !dueTo.value || dueAt && dueAt <= new Date(`${dueTo.value}T23:59:59Z`);
    const matchesStatus = !selectedStatus.value || row.status === selectedStatus.value;
    const matchesType = !selectedType.value || row.saleType === selectedType.value;
    return matchesQuery && matchesFrom && matchesTo && matchesStatus && matchesType;
  });
});
const selected = computed(
  () => rows.value.find(row => row.id === selectedId.value) || null
);
const tableTotal = computed(() => props.total || rows.value.length);

const metricPalette = [
  ['i-lucide-receipt-text', 'bg-teal-50 text-[#0ca99d]'],
  ['i-lucide-arrow-down-to-line', 'bg-violet-50 text-[#5a24c7]'],
  ['i-lucide-circle-check-big', 'bg-emerald-50 text-emerald-600'],
  ['i-lucide-landmark', 'bg-orange-50 text-orange-500'],
];
const kpis = computed(() =>
  Array.from({ length: 4 }, (_, index) => {
    const metric = sourceMetrics.value[index];
    const [fallbackIcon, tone] = metricPalette[index];
    if (!metric) {
      return {
        detail: 'Indicador indisponível',
        icon: fallbackIcon,
        label: 'Sem indicador',
        tone,
        value: '—',
      };
    }
    return {
      detail: `${rows.value.length} parcelas carregadas`,
      icon: metric.icon || fallbackIcon,
      label: metric.label || 'Indicador financeiro',
      tone,
      value: metric.value || '—',
    };
  })
);
const statusDistribution = computed(() => {
  const counts = new Map();
  rows.value.forEach(row => {
    const label = row.status || 'Sem status';
    counts.set(label, (counts.get(label) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([label, count]) => ({
      count,
      label,
      percentage: rows.value.length ? Math.round((count / rows.value.length) * 100) : 0,
    }))
    .sort((first, second) => second.count - first.count)
    .slice(0, 4);
});
const monthlySeries = computed(() =>
  asArray(
    firstValue(financeData.value, [
      'summary.serie_mensal',
      'summary.monthly_series',
      'summary.cash_flow',
      'summary.fluxo_caixa',
    ])
  )
    .map((item, index) => ({
      balance: formatMoney(firstValue(item, ['saldo', 'balance'])),
      id: String(item.id || item.mes || item.month || index),
      label: firstValue(item, ['mes', 'month', 'label']),
      payable: formatMoney(firstValue(item, ['a_pagar', 'payable', 'expenses'])),
      receivable: formatMoney(firstValue(item, ['a_receber', 'receivable', 'income'])),
    }))
    .filter(item => item.label)
    .slice(0, 6)
);
const events = computed(() =>
  sourceHistory.value
    .map((event, index) => ({
      actor: firstValue(event, ['actor', 'ator.name', 'user.name', 'author.name'], 'Sistema'),
      date: firstValue(event, [
        'date',
        'ocorrido_em',
        'occurred_at',
        'created_at',
        'updated_at',
        'at',
      ]),
      id: String(event.id || event.sequence || event.sequencia || index),
      label: firstValue(
        event,
        ['label', 'action', 'event_type', 'tipo', 'status_novo'],
        'Atualização registrada'
      ),
    }))
    .slice(0, 5)
);
const documents = computed(() => {
  if (!selected.value) return [];
  return asArray(
    selected.value.source.responsaveis_pagamento ||
      selected.value.source.parcela?.responsaveis_pagamento
  )
    .map(responsibility => ({
      id: String(responsibility.boleto?.documento_id || responsibility.boleto_id || responsibility.id),
      name: firstValue(responsibility, ['boleto.nome', 'nome_boleto']),
      status: firstValue(responsibility, ['boleto.enviado', 'boleto.status']),
    }))
    .filter(document => document.name);
});
const selectedFacts = computed(() => {
  if (!selected.value) return [];
  return [
    { label: 'Valor da parcela', value: selected.value.amount },
    { label: 'Vencimento', value: formatDate(selected.value.dueAt) },
    { label: 'Modalidade', value: displayText(selected.value.saleType) },
    { label: 'Centro de custo', value: displayText(selected.value.store) },
    { label: 'Responsável', value: displayText(selected.value.beneficiary || selected.value.manager) },
  ];
});
const gamificationPanel = computed(() => {
  const source = firstValue(
    financeData.value,
    [
      'summary.gamificacao',
      'summary.gamification',
      'gamificacao',
      'gamification',
    ],
    {}
  );
  const level = firstValue(source, [
    'nivel.label',
    'level.label',
    'nivel',
    'level',
  ]);
  const specialty = firstValue(source, [
    'especialidade',
    'specialty',
    'cargo',
    'role',
  ]);
  const points = firstValue(source, [
    'pontos',
    'points',
    'total_pontos',
    'total_points',
  ]);
  const streak = firstValue(source, [
    'sequencia.dias',
    'streak.days',
    'sequencia',
    'streak',
  ]);
  const progressValue = firstValue(source, [
    'progresso.percentual',
    'progress.percentage',
    'progresso',
    'progress',
  ]);
  const progress =
    progressValue === '' || progressValue === null
      ? Number.NaN
      : Number(progressValue);
  const rules = asArray(
    firstValue(source, ['regras', 'rules', 'pontuacoes', 'scoring_rules'], [])
  )
    .map((rule, index) => ({
      id: String(rule.id || rule.code || index),
      label: firstValue(rule, ['label', 'titulo', 'title', 'nome', 'name']),
      points: firstValue(rule, ['pontos', 'points', 'valor', 'value']),
    }))
    .filter(rule => rule.label)
    .slice(0, 3);

  return {
    available: Boolean(level || specialty || points || streak || rules.length),
    level: level ? `Nível ${displayText(level)}` : 'Nível indisponível',
    points: points === '' ? '—' : displayText(points),
    progressClass: progressClassFor(progress),
    rules,
    specialty: specialty || 'Dados financeiros reais',
    streak: streak === '' ? '—' : displayText(streak),
  };
});

const selectRow = id => {
  selectedId.value = id;
};
const openDetail = () => {
  if (!selected.value?.id) return;
  emit('navigate', { id: selected.value.id, screen: 'detail' });
};
</script>

<template>
  <main
    data-imudei-visual-id="finance-dashboard-root"
    class="mx-auto w-full max-w-[1328px] text-[#101958]"
  >
    <section class="grid grid-cols-[minmax(0,1fr)_352px] gap-6">
      <section class="min-w-0 space-y-3">
        <section
          data-imudei-visual-id="finance-dashboard-header"
          class="overflow-hidden rounded-2xl border border-[#ece8f4] bg-white shadow-[0_3px_15px_rgba(50,29,96,0.05)]"
        >
          <div class="flex min-h-[88px] items-center justify-between gap-5 px-5">
            <div class="flex min-w-0 items-center gap-4">
              <i class="grid size-12 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]">
                <i class="i-lucide-badge-dollar-sign size-6" />
              </i>
              <span class="min-w-0">
                <strong class="block text-[13px]">{{ gamificationPanel.level }}</strong>
                <small class="mt-1 block text-[9px] text-[#293a77]">{{ gamificationPanel.specialty }}</small>
                <span class="mt-2 block h-1 w-40 rounded-full bg-[#ebe7f1]">
                  <i class="block h-full rounded-full bg-[#5a24c7]" :class="gamificationPanel.progressClass" />
                </span>
              </span>
            </div>
            <div class="flex shrink-0 items-center gap-5 text-[8px] text-[#293a77]">
              <span class="border-l border-[#e8e3ef] pl-5">
                <i class="i-lucide-flame mr-2 inline size-5 text-orange-500" />
                <b class="block text-[12px] text-[#101958]">{{ gamificationPanel.streak }}</b>
                sequência
              </span>
              <span class="border-l border-[#e8e3ef] pl-5">
                <i class="i-lucide-gem mr-2 inline size-5 text-[#5a24c7]" />
                <b class="block text-[15px] text-[#101958]">{{ gamificationPanel.points }}</b>
                pontos
              </span>
              <button class="rounded-lg border border-[#e8e3ef] px-3 py-2 font-semibold text-[#5a24c7]" disabled type="button">
                Prêmios
              </button>
              <button class="rounded-lg border border-[#e8e3ef] px-3 py-2 font-semibold text-[#5a24c7]" disabled type="button">
                Regras dos pontos
              </button>
            </div>
          </div>
          <div class="flex min-h-[43px] items-center gap-3 border-t border-[#eeeaf4] px-5 text-[8px]">
            <b class="shrink-0">Você pontua assim:</b>
            <template v-if="gamificationPanel.rules.length">
              <span v-for="rule in gamificationPanel.rules" :key="rule.id" class="rounded-lg bg-[#f2fbfa] px-3 py-2 text-[#273875]">
                {{ rule.label }} <b v-if="rule.points !== ''" class="ml-2 text-emerald-600">+{{ rule.points }}</b>
              </span>
            </template>
            <span v-else class="min-w-0 truncate text-[#7480a0]">
              {{ gamificationPanel.available ? 'A API não retornou regras de pontuação para este painel.' : 'A API não retornou pontos ou regras para este painel.' }}
            </span>
          </div>
        </section>

        <p
          v-if="loading || error || props.preview"
          class="rounded-xl border px-4 py-2 text-[8px]"
          :class="error ? 'border-rose-100 bg-rose-50 text-rose-600' : 'border-violet-100 bg-violet-50 text-[#5a24c7]'"
        >
          {{ error || (loading ? 'Carregando lançamentos financeiros retornados pela API…' : 'A prévia não utiliza lançamentos fictícios.') }}
        </p>

        <section data-imudei-visual-id="finance-dashboard-metrics" class="grid grid-cols-4 gap-3">
          <article
            v-for="metric in kpis"
            :key="metric.label"
            class="min-h-[134px] rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_3px_15px_rgba(50,29,96,0.05)]"
          >
            <div class="flex items-center gap-3">
              <i class="grid size-10 place-items-center rounded-full" :class="metric.tone">
                <i :class="metric.icon" class="size-5" />
              </i>
              <span class="min-w-0">
                <small class="block truncate text-[9px] text-[#37406d]">{{ metric.label }}</small>
                <strong class="mt-1 block truncate text-[16px]">{{ metric.value }}</strong>
              </span>
            </div>
            <small class="mt-5 block text-[8px] text-[#69759c]">{{ metric.detail }}</small>
            <span class="mt-3 block h-1 rounded-full bg-[#f0edf6]"><i class="block h-full w-1/2 rounded-full bg-[#d6c7ff]" /></span>
          </article>
        </section>

        <nav class="flex gap-8 border-b border-[#eeeaf4] px-3">
          <span class="border-b-2 border-[#5a24c7] px-1 py-3 text-[9px] font-bold text-[#5a24c7]">Visão geral</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#69759c]">Recebimentos</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#69759c]">Pagamentos</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#69759c]">Comissões</span>
        </nav>

        <section data-imudei-visual-id="finance-dashboard-charts" class="grid grid-cols-[1.35fr_.95fr] gap-3">
          <article data-imudei-visual-id="finance-dashboard-cash-flow" class="min-h-[262px] rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_3px_15px_rgba(50,29,96,0.05)]">
            <header class="flex items-center justify-between"><h2 class="text-[13px] font-bold">Fluxo de caixa</h2><span class="rounded-lg border border-[#e8e3ef] px-3 py-2 text-[8px] text-[#69759c]">Período da rota</span></header>
            <div class="mt-3 flex gap-5 text-[8px]"><span class="flex items-center gap-2"><i class="size-2 rounded-sm bg-[#0ea89c]" />A receber</span><span class="flex items-center gap-2"><i class="size-2 rounded-sm bg-[#f97316]" />A pagar</span><span class="flex items-center gap-2"><i class="size-2 rounded-sm bg-[#351b98]" />Saldo</span></div>
            <div v-if="monthlySeries.length" class="mt-5 divide-y divide-[#eeeaf4] rounded-xl border border-[#eeeaf4] px-3"><div v-for="month in monthlySeries" :key="month.id" class="grid grid-cols-[.6fr_1fr_1fr_1fr] gap-3 py-3 text-[8px]"><strong>{{ month.label }}</strong><span class="text-[#0ea89c]">{{ month.receivable }}</span><span class="text-orange-500">{{ month.payable }}</span><span class="text-[#351b98]">{{ month.balance }}</span></div></div>
            <div v-else class="mt-5 grid h-[157px] grid-cols-3 gap-2 rounded-xl border border-dashed border-[#e5e0ed] p-3 text-center">
              <span class="grid place-items-center rounded-lg bg-teal-50 px-2"><i class="i-lucide-arrow-down-to-line size-4 text-[#0ea89c]" /><b class="mt-1 block text-[8px]">Recebimentos</b><small class="mt-1 block text-[7px] text-[#7480a0]">Agregado não retornado</small></span>
              <span class="grid place-items-center rounded-lg bg-orange-50 px-2"><i class="i-lucide-arrow-up-to-line size-4 text-orange-500" /><b class="mt-1 block text-[8px]">Pagamentos</b><small class="mt-1 block text-[7px] text-[#7480a0]">Agregado não retornado</small></span>
              <span class="grid place-items-center rounded-lg bg-violet-50 px-2"><i class="i-lucide-scale size-4 text-[#5a24c7]" /><b class="mt-1 block text-[8px]">Saldo</b><small class="mt-1 block text-[7px] text-[#7480a0]">Agregado não retornado</small></span>
              <span class="col-span-3 flex items-center justify-center gap-2 rounded-lg bg-[#fcfbff] px-3 text-left"><i class="i-lucide-chart-no-axes-combined size-4 text-[#a58add]" /><span><b class="block text-[8px]">Série mensal indisponível</b><small class="block text-[7px] text-[#7480a0]">A API retornou parcelas, mas não um agregado mensal.</small></span></span>
            </div>
          </article>

          <article data-imudei-visual-id="finance-dashboard-status" class="min-h-[262px] rounded-2xl border border-[#ebe7f1] bg-white p-4 shadow-[0_3px_15px_rgba(50,29,96,0.05)]">
            <h2 class="text-[13px] font-bold">Status das parcelas</h2>
            <div class="mt-6 flex items-center gap-5"><div class="grid size-32 shrink-0 place-items-center rounded-full border-[18px] border-violet-100"><span class="text-center"><b class="block text-[20px]">{{ rows.length }}</b><small class="text-[8px] text-[#7480a0]">parcelas</small></span></div><div class="min-w-0 space-y-3"><span v-for="(entry, index) in statusDistribution" :key="entry.label" class="flex items-center gap-2 text-[8px]"><i class="size-2 shrink-0 rounded-full" :class="dotTone(index)" /><span class="truncate">{{ entry.label }}</span><b class="ml-auto">{{ entry.count }} ({{ entry.percentage }}%)</b></span><small v-if="!statusDistribution.length" class="block text-[8px] text-[#7480a0]">Sem status retornado.</small></div></div>
            <div v-if="statusDistribution.length < 2" class="mt-5 flex items-center gap-2 rounded-xl border border-dashed border-[#e5e0ed] bg-[#fcfbff] px-3 py-2"><i class="i-lucide-chart-pie size-4 text-[#a58add]" /><span><b class="block text-[8px]">Distribuição parcial</b><small class="block text-[7px] text-[#7480a0]">A API retornou {{ statusDistribution.length }} categoria de status para {{ rows.length }} parcela(s).</small></span></div>
          </article>
        </section>

        <section data-imudei-visual-id="finance-dashboard-table" class="flex min-h-[342px] flex-col rounded-2xl border border-[#ebe7f1] bg-white p-3 shadow-[0_3px_15px_rgba(50,29,96,0.05)]">
          <header class="flex items-center gap-3"><label class="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#e6e1ec] px-3 text-[#69759c]"><i class="i-lucide-search size-4" /><input v-model="searchQuery" class="min-w-0 flex-1 bg-transparent text-[9px] text-[#101958] outline-none placeholder:text-[#9aa2b9]" placeholder="Buscar por imóvel, favorecido ou parcela" type="search" /></label><label class="flex h-9 items-center gap-2 rounded-lg border border-[#e6e1ec] px-3 text-[8px] text-[#69759c]"><i class="i-lucide-calendar-days size-3.5" /><input v-model="dueFrom" class="w-[74px] bg-transparent text-[8px] text-[#101958] outline-none" aria-label="Vencimento inicial" type="date" /><span>—</span><input v-model="dueTo" class="w-[74px] bg-transparent text-[8px] text-[#101958] outline-none" aria-label="Vencimento final" type="date" /></label><label class="flex h-9 items-center gap-2 rounded-lg border border-[#e6e1ec] px-3 text-[8px] text-[#69759c]"><i class="i-lucide-tags size-3.5" /><select v-model="selectedType" class="bg-transparent text-[8px] text-[#101958] outline-none"><option value="">Tipo: todos</option><option v-for="type in typeOptions" :key="type" :value="type">{{ type }}</option></select></label><label class="flex h-9 items-center gap-2 rounded-lg border border-[#e6e1ec] px-3 text-[8px] text-[#69759c]"><i class="i-lucide-filter size-3.5" /><select v-model="selectedStatus" class="bg-transparent text-[8px] text-[#101958] outline-none"><option value="">Status: todos</option><option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option></select></label></header>
          <div class="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#eeeaf4]">
            <div class="grid grid-cols-[1.25fr_.55fr_1fr_.85fr_.8fr_.65fr_.25fr] items-center gap-2 bg-[#fcfbff] px-4 py-3 text-[8px] font-bold text-[#26366f]"><span>Venda</span><span>Parcela</span><span>Favorecido</span><span>Previsto</span><span>Valor</span><span>Status</span><span>Ações</span></div>
            <p v-if="loading" class="flex flex-1 items-center justify-center px-4 py-10 text-center text-[9px] text-[#7480a0]">Carregando parcelas retornadas pela API…</p>
            <p v-else-if="!filteredRows.length" class="flex flex-1 items-center justify-center px-4 py-10 text-center text-[9px] text-[#7480a0]">Nenhum lançamento financeiro corresponde aos filtros atuais.</p>
            <template v-else>
              <button v-for="row in filteredRows" :key="row.id" class="grid min-h-[61px] w-full grid-cols-[1.25fr_.55fr_1fr_.85fr_.8fr_.65fr_.25fr] items-center gap-2 border-t border-[#eeeaf4] px-4 text-left text-[8px]" :class="selected?.id === row.id ? 'bg-violet-50' : 'hover:bg-[#fcfbff]'" type="button" @click="selectRow(row.id)"><span class="flex min-w-0 items-center gap-2"><i class="grid size-9 shrink-0 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]"><i class="i-lucide-building-2 size-4" /></i><span class="min-w-0"><b class="block truncate text-[9px] text-[#1735ba]">{{ row.reference || `#${row.id}` }}</b><small class="block truncate text-[7px] text-[#627098]">{{ row.property }}</small></span></span><b>{{ row.installment ? `${row.installment}${row.installmentTotal ? ` / ${row.installmentTotal}` : ''}` : '—' }}</b><span class="flex min-w-0 items-center gap-2"><i class="grid size-7 shrink-0 place-items-center rounded-full bg-[#4720b9] text-[7px] font-bold text-white">{{ initials(row.beneficiary) }}</i><b class="truncate text-[8px]">{{ row.beneficiary || 'Não informado' }}</b></span><span>{{ formatDate(row.dueAt) }}</span><b>{{ row.amount }}</b><span class="w-fit rounded-md px-2 py-1 text-[7px] font-semibold" :class="statusTone(row.status)">{{ row.status }}</span><i class="i-lucide-eye size-4 text-[#4e22be]" /></button>
              <div v-if="filteredRows.length < 5" class="flex flex-1 items-center justify-center border-t border-dashed border-[#eeeaf4] bg-[#fcfbff] px-4 text-center"><span><i class="i-lucide-list-collapse mx-auto size-5 text-[#a58add]" /><b class="mt-2 block text-[8px]">Não há outros lançamentos retornados</b><small class="mt-1 block text-[7px] text-[#7480a0]">A API disponibilizou {{ filteredRows.length }} registro(s) para os filtros atuais.</small></span></div>
            </template>
          </div>
          <footer class="mt-auto flex pt-3 items-center justify-between text-[8px] text-[#7480a0]"><span>Mostrando {{ filteredRows.length }} de {{ tableTotal }} parcelas</span><span v-if="tableTotal > rows.length">A paginação é controlada pela rota atual.</span></footer>
        </section>
      </section>

      <aside data-imudei-visual-id="finance-dashboard-detail" class="-my-4 flex min-h-[1024px] flex-col border border-y-0 border-r-0 border-[#e8e4ef] bg-white p-5">
        <template v-if="selected">
          <header class="flex items-center justify-between gap-3"><h2 class="text-[12px] font-bold">Visualização rápida</h2><span class="rounded-md bg-emerald-50 px-2 py-1 text-[8px] font-semibold text-emerald-700"><i class="i-lucide-shield-check mr-1 inline size-3" />Auditoria</span><button class="grid size-7 place-items-center rounded-lg text-[#5d6687]" type="button" aria-label="Fechar visualização" @click="selectedId = ''"><i class="i-lucide-x size-4" /></button></header>
          <div class="mt-5 flex items-start justify-between gap-3"><span class="min-w-0"><h3 class="truncate text-[16px] font-bold">{{ selected.reference || `#${selected.id}` }}</h3><small class="mt-1 block text-[8px] text-[#69759c]">Parcela #{{ selected.id }}</small></span><span class="shrink-0 rounded-md px-2 py-1 text-[8px] font-semibold" :class="statusTone(selected.status)">{{ selected.status }}</span></div>
          <div class="mt-5 flex gap-3 border-b border-[#eeeaf4] pb-4"><i class="grid size-16 shrink-0 place-items-center rounded-xl bg-violet-50 text-[#5a24c7]"><i class="i-lucide-building-2 size-6" /></i><span class="min-w-0"><strong class="block text-[10px]">{{ selected.property }}</strong><small class="mt-2 block text-[8px] text-[#7180a4]">{{ selected.propertyAddress || 'Endereço não retornado' }}</small></span></div>
          <dl class="mt-4 space-y-3 border-b border-[#eeeaf4] pb-4 text-[9px]"><div v-for="fact in selectedFacts" :key="fact.label" class="flex items-start justify-between gap-4"><dt class="text-[#66739b]">{{ fact.label }}</dt><dd class="max-w-[58%] text-right font-semibold">{{ fact.value }}</dd></div></dl>
          <article class="mt-4 rounded-xl border border-[#e8e4ef] p-3"><h3 class="text-[9px] font-bold">Comprovantes</h3><p v-if="!documents.length" class="mt-3 text-[8px] text-[#7480a0]">A listagem não retornou comprovantes para esta parcela.</p><div v-else><div v-for="document in documents" :key="document.id" class="mt-3 flex items-center gap-2"><i class="i-lucide-file-text size-4 text-[#5a24c7]" /><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ document.name }}</strong><small class="block text-[7px] text-[#7480a0]">{{ displayText(document.status) }}</small></span></div></div></article>
          <article class="mt-4 rounded-xl border border-[#e8e4ef] p-3"><h3 class="text-[9px] font-bold">Histórico financeiro</h3><p v-if="!events.length" class="mt-3 text-[8px] text-[#7480a0]">A API não retornou lembretes ou eventos financeiros para a rota atual.</p><div v-else><div v-for="event in events.slice(0, 2)" :key="event.id" class="mt-3 flex gap-2"><i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">{{ initials(event.actor) }}</i><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ event.label }}</strong><small class="block truncate text-[7px] text-[#7480a0]">{{ event.actor }} · {{ formatDateTime(event.date) }}</small></span></div></div></article>
          <article class="mt-4 rounded-xl border border-[#e8e4ef] p-3"><h3 class="text-[9px] font-bold">Auditoria e integridade</h3><p v-if="events.length < 3" class="mt-3 text-[8px] text-[#7480a0]">A API não retornou eventos de auditoria adicionais para a rota atual.</p><div v-else><div v-for="event in events.slice(2, 5)" :key="event.id" class="mt-3 flex gap-2"><i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">{{ initials(event.actor) }}</i><span class="min-w-0 flex-1"><strong class="block truncate text-[8px]">{{ event.label }}</strong><small class="block truncate text-[7px] text-[#7480a0]">{{ event.actor }} · {{ formatDateTime(event.date) }}</small></span></div></div></article>
          <article class="mt-4 rounded-xl border border-violet-100 bg-violet-50 p-3"><div class="flex gap-2"><i class="i-lucide-shield-check mt-0.5 size-4 text-[#5a24c7]" /><span><strong class="block text-[8px]">Ações protegidas por permissão</strong><small class="mt-1 block text-[7px] leading-3 text-[#69759c]">Abra o detalhe para consultar ações retornadas pela API para esta parcela.</small></span></div></article>
          <footer data-imudei-visual-id="finance-dashboard-footer" class="mt-auto pt-5"><button class="h-11 w-full rounded-lg bg-[#5a24c7] text-[9px] font-bold text-white shadow-[0_8px_18px_rgba(90,36,199,0.25)]" type="button" @click="openDetail">Abrir detalhes</button><article class="mt-3 flex gap-2 rounded-xl border border-violet-100 bg-violet-50 p-3"><i class="i-lucide-info mt-0.5 size-4 text-[#5a24c7]" /><span><strong class="block text-[8px]">Disponibilidade operacional</strong><small class="mt-1 block text-[7px] leading-3 text-[#69759c]">Ações, documentos e auditoria aparecem somente quando retornados pela API.</small></span></article></footer>
        </template>
        <div v-else class="grid h-full min-h-[650px] place-items-center text-center"><span><i class="i-lucide-panel-right-open mx-auto size-8 text-[#a58add]" /><strong class="mt-3 block text-[10px]">Selecione um lançamento</strong><small class="mt-1 block text-[8px] text-[#7480a0]">Dados financeiros reais aparecerão aqui.</small></span></div>
      </aside>
    </section>
  </main>
</template>
